import { useMemo, useState } from 'react'
import { getEmpresas } from '../../../../services/localstorage/localstorage.service'
import { getPeriodoMesAtual } from '../../../../services/util/date.service'
import { errorHandler } from '../../../../services/util/error.service'
import ProdutividadeExpedicaoHttp from '../../../../services/api/https/dashboard/produtividade-expedicao.http'
import { ExpedicaoStatus } from '../../../../constants/expedicao-status'

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  FETCH_INITIAL_INFO: 'Adquirindo empresas',
  GET_EXPEDICOES: 'Adquirindo dados de produtividade',
}

const INITIAL_STATE = {
  companies: [],
  companySelected: null,
  pInicial: getPeriodoMesAtual().dataInicial,
  pFinal: getPeriodoMesAtual().dataFinal,
  loadingMessage: LOAD_MESSAGE.NONE,
  errorMessage: null,
  successMessage: null,

  // dados retornados da API
  expedicoes: [],
  expedicaoOptions: [],
  selectedExpedicoes: [],

  // dependentes
  userOptions: [],
  selectedUsers: [],

  report: null,
}

const EXPEDICAO_STATUS_CONFIG = [
  ExpedicaoStatus.Expedido,
  ExpedicaoStatus.Finalizado,
  ExpedicaoStatus.EmConferencia,
  ExpedicaoStatus.Conferido,
]

// helpers de tempo
function timeStrToSeconds(str) {
  if (!str || str === '-' || str === '00:00:00') return 0
  const parts = str.split(':')
  if (parts.length !== 3) return 0
  const [h, m, s] = parts.map(n => parseInt(n, 10) || 0)
  return h * 3600 + m * 60 + s
}

function secondsToTimeStr(totalSeconds) {
  const sec = Math.max(0, Math.floor(totalSeconds))
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  const pad = n => n.toString().padStart(2, '0')
  return `${pad(h)}:${pad(m)}:${pad(s)}`
}

function formatDateTimeToBR(dateTimeStr) {
  if (!dateTimeStr || dateTimeStr === '-') return '-'
  const parts = dateTimeStr.split(' ')
  if (parts.length !== 2) return dateTimeStr

  const [datePart, timePart] = parts
  const dateParts = datePart.split('-')
  if (dateParts.length !== 3) return dateTimeStr

  const [year, month, day] = dateParts
  return `${day}/${month}/${year} ${timePart}`
}

export default function useProdutividadeExpedicao() {
  const [state, setState] = useState(INITIAL_STATE)

  const localDispatch = useMemo(() => {
    return {
      resetState() {
        setState(prev => ({
          ...prev,
          errorMessage: null,
          successMessage: null,
          loadingMessage: LOAD_MESSAGE.NONE,
        }))
      },

      onInit() {
        setState(prev => ({ ...prev, loadingMessage: LOAD_MESSAGE.FETCH_INITIAL_INFO }))
      },

      getCompaniesAssociatedToUser() {
        try {
          const empresas = getEmpresas()
          setState(prev => ({
            ...prev,
            companies: empresas,
            companySelected: empresas && empresas.length === 1 ? empresas[0] : prev.companySelected,
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
        } catch (e) {
          setState(prev => ({
            ...prev,
            errorMessage: errorHandler(e),
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
        }
      },

      onCompanyChange(empresa) {
        setState(prev => ({
          ...prev,
          companySelected: empresa,
        }))
      },

      onPeriodoInicialChange(pInicial) {
        if (!pInicial) return

        let dateValue
        if (pInicial instanceof Date) {
          dateValue = pInicial
        } else if (typeof pInicial === 'string') {
          dateValue = new Date(pInicial)
        } else {
          return
        }

        if (isNaN(dateValue.getTime())) return

        setState(prev => ({ ...prev, pInicial: dateValue }))
      },

      onPeriodoFinalChange(pFinal) {
        if (!pFinal) return

        let dateValue
        if (pFinal instanceof Date) {
          dateValue = pFinal
        } else if (typeof pFinal === 'string') {
          dateValue = new Date(pFinal)
        } else {
          return
        }

        if (isNaN(dateValue.getTime())) return

        setState(prev => ({ ...prev, pFinal: dateValue }))
      },

      onBtnPesquisar(companySelected, tpId, pInicial, pFinal) {
        if (!companySelected) {
          setState(prev => ({ ...prev, errorMessage: 'Selecione uma empresa' }))
          return
        }
        if (!tpId || tpId === 0) {
          setState(prev => ({ ...prev, errorMessage: 'Selecione um TP na barra superior' }))
          return
        }
        if (!(pInicial instanceof Date) || isNaN(pInicial.getTime())) {
          setState(prev => ({ ...prev, errorMessage: 'Período inicial inválido' }))
          return
        }
        if (!(pFinal instanceof Date) || isNaN(pFinal.getTime())) {
          setState(prev => ({ ...prev, errorMessage: 'Período final inválido' }))
          return
        }

        setState(prev => ({ ...prev, loadingMessage: LOAD_MESSAGE.GET_EXPEDICOES }))
      },

      async getExpedicoesPorEmpresa(companyId, tpId, pInicial, pFinal) {
        try {
          const data = await ProdutividadeExpedicaoHttp.getExpedicoesPorEmpresa(
            companyId,
            tpId,
            pInicial,
            pFinal,
          )

          const expedicoes = Array.isArray(data.expedicoes) ? data.expedicoes : []

          // Verifica se não há expedições no período
          if (expedicoes.length === 0) {
            setState(prev => ({
              ...prev,
              expedicoes: [],
              expedicaoOptions: [],
              selectedExpedicoes: [],
              userOptions: [],
              selectedUsers: [],
              report: null,
              loadingMessage: LOAD_MESSAGE.NONE,
              errorMessage: 'Nenhuma expedição com conferência no período selecionado',
            }))
            return
          }

          const expedicaoOptions = expedicoes.map(e => ({
            label: `${e.id} - ${e.name} (${e.status})`,
            value: e.id,
          }))

          setState(prev => ({
            ...prev,
            expedicoes,
            expedicaoOptions,
            selectedExpedicoes: [],
            userOptions: [],
            selectedUsers: [],

            report: null,
            loadingMessage: LOAD_MESSAGE.NONE,
            successMessage: 'Dados carregados. Selecione as expedições.',
          }))
        } catch (e) {
          setState(prev => ({
            ...prev,
            errorMessage: errorHandler(e),
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
        }
      },

      onExpedicoesChange(selectedExpedicoes) {
        setState(prev => {
          const selectedExpedicaoIds = selectedExpedicoes.map(e => e.value)
          const expedicoesSelecionadas = prev.expedicoes.filter(e =>
            selectedExpedicaoIds.includes(e.id),
          )

          const usersMap = new Map()
          expedicoesSelecionadas.forEach(e => {
            ;(e.users || []).forEach(u => {
              if (!usersMap.has(u.id)) {
                usersMap.set(u.id, { id: u.id, name: u.name })
              }
            })
          })

          const userOptions = Array.from(usersMap.values()).map(u => ({
            label: u.name,
            value: u.id,
          }))

          return {
            ...prev,
            selectedExpedicoes,
            userOptions,
            selectedUsers: userOptions,
          }
        })
      },

      onUsersChange(selectedUsers) {
        setState(prev => ({
          ...prev,
          selectedUsers,
        }))
      },

      onGerarRelatorio() {
        setState(prev => {
          if (prev.selectedExpedicoes.length === 0) {
            return { ...prev, errorMessage: 'Selecione ao menos uma expedição.' }
          }

          const selectedExpedicaoIds = prev.selectedExpedicoes.map(e => e.value)
          const selectedUserIds = prev.selectedUsers.map(u => u.value)

          const expedicoesSelecionadas = prev.expedicoes.filter(e =>
            selectedExpedicaoIds.includes(e.id),
          )

          // ===== Seção Separação =====
          let totalVolumes = 0
          let volumesSeparados = 0

          expedicoesSelecionadas.forEach(e => {
            totalVolumes += Number(e.qtdVolumes || 0)
            volumesSeparados += Number(e.volSeparados || 0)
          })

          totalVolumes = Number(totalVolumes.toFixed(2))
          volumesSeparados = Number(volumesSeparados.toFixed(2))

          const percentVolSeparado = totalVolumes > 0 ? (volumesSeparados / totalVolumes) * 100 : 0

          const statusCount = new Map()

          expedicoesSelecionadas.forEach(e => {
            statusCount.set(e.status, (statusCount.get(e.status) || 0) + 1)
          })

          const statusPieData = [
            ...EXPEDICAO_STATUS_CONFIG.filter(status => statusCount.has(status.label)).map(
              status => ({
                name: status.label,
                value: statusCount.get(status.label),
                color: status.color,
              }),
            ),
            ...Array.from(statusCount.entries())
              .filter(
                ([statusName]) =>
                  !EXPEDICAO_STATUS_CONFIG.some(status => status.label === statusName),
              )
              .map(([statusName, value]) => ({
                name: statusName,
                value,
                color: '#999999',
              })),
          ]

          // ===== Seção Tempos =====
          let tempoTotalSec = 0
          let totalParadoSec = 0
          let paradasAcima10Total = 0

          expedicoesSelecionadas.forEach(e => {
            tempoTotalSec += timeStrToSeconds(e.duracao)
            totalParadoSec += timeStrToSeconds(e.totalParado)
            paradasAcima10Total += Number(e.paradas10min || 0)
          })

          let maiorParadaSec = 0
          let inicioMaiorParada = '-'
          let fimMaiorParada = '-'

          expedicoesSelecionadas.forEach(e => {
            const mp = timeStrToSeconds(e.maiorParada)
            if (mp > maiorParadaSec) {
              maiorParadaSec = mp
              inicioMaiorParada = formatDateTimeToBR(e.inicioMaiorParada)
              fimMaiorParada = formatDateTimeToBR(e.fimMaiorParada)
            }
          })

          const tempoLiquidoSec = Math.max(0, tempoTotalSec - totalParadoSec)

          // ===== Seção Produtividade =====
          const usuariosSelecionados = new Set(selectedUserIds)
          const produtividadeMap = new Map()

          expedicoesSelecionadas.forEach(e => {
            ;(e.users || []).forEach(u => {
              if (!usuariosSelecionados.has(u.id)) return

              const atual = produtividadeMap.get(u.id) || {
                id: u.id,
                name: u.name,
                qtdSeparadas: 0,
                pesoTotal: 0,
                tempoSec: 0,
              }

              atual.qtdSeparadas += Number(u.qtdBipada || 0)
              atual.pesoTotal += Number(u.pesoTotal || 0)
              atual.tempoSec += timeStrToSeconds(u.tempoGasto)

              produtividadeMap.set(u.id, atual)
            })
          })

          const produtividade = Array.from(produtividadeMap.values()).map(u => {
            const tempoMin = u.tempoSec > 0 ? u.tempoSec / 60 : 0
            const bipsMin = tempoMin > 0 ? u.qtdSeparadas / tempoMin : 0
            const qtdSeparadas = u.qtdSeparadas.toFixed(2)
            return {
              ...u,
              qtdSeparadas,
              pesoTotal: u.pesoTotal.toFixed(2),
              tempo: secondsToTimeStr(u.tempoSec),
              bipsMin: Number.isFinite(bipsMin) ? bipsMin.toFixed(2) : '0.00',
            }
          })

          const report = {
            separacao: {
              statusPieData,
              statusPieColors: statusPieData.map(item => item.color),
              totalVolumes: totalVolumes.toFixed(2),
              volumesSeparados: volumesSeparados.toFixed(2),
              percentVolSeparado: percentVolSeparado.toFixed(2),
            },
            tempos: {
              tempoTotal: secondsToTimeStr(tempoTotalSec),
              paradasAcima10: paradasAcima10Total,
              tempoTotalParado: secondsToTimeStr(totalParadoSec),
              maiorParada: secondsToTimeStr(maiorParadaSec),
              inicioMaiorParada,
              fimMaiorParada,
              tempoLiquido: secondsToTimeStr(tempoLiquidoSec),
            },
            produtividade,
          }

          return {
            ...prev,
            report,
            errorMessage: null,
            successMessage: null,
          }
        })
      },
    }
  }, [])

  return { state, localDispatch }
}
